import { useState, useEffect } from "react";
import { useAuth } from "contexts/AuthContext";
import {
  Filter, Trash2, Check, X, ExternalLink, ArrowUp, MessageSquare,
  ChevronDown, ChevronUp, Loader2, RotateCcw
} from "lucide-react";

export default function FilteredPostsTab({ selectedProject }) {
  const { api } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [actionLoading, setActionLoading] = useState({});

  useEffect(() => {
    fetchPosts();
  }, [selectedProject]);

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const params = selectedProject ? { project_id: selectedProject } : {};
      const res = await api.get("/import/filtered-posts", { params });
      setPosts(res.data);
    } catch {
      setPosts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (postId) => {
    setActionLoading(p => ({ ...p, [postId]: "approve" }));
    try {
      await api.post(`/import/filtered-posts/${postId}/approve`);
      setPosts(prev => prev.filter(p => p.id !== postId));
    } catch {} finally {
      setActionLoading(p => ({ ...p, [postId]: null }));
    }
  };

  const handleDelete = async (postId) => {
    setActionLoading(p => ({ ...p, [postId]: "delete" }));
    try {
      await api.delete(`/import/filtered-posts/${postId}`);
      setPosts(prev => prev.filter(p => p.id !== postId));
    } catch {} finally {
      setActionLoading(p => ({ ...p, [postId]: null }));
    }
  };

  if (loading) {
    return <div className="loading" data-testid="filtered-loading"><div className="spinner"></div></div>;
  }

  return (
    <div className="filtered-posts-section" data-testid="filtered-posts-section">
      <div className="section-header">
        <h2 className="section-title"><Filter size={20} color="#ff4500" /> Filtered Posts ({posts.length})</h2>
        <button className="btn btn-sm btn-secondary" onClick={fetchPosts} data-testid="refresh-filtered">
          <RotateCcw size={14} /> Refresh
        </button>
      </div>
      <p className="section-desc">Posts filtered out by AI during import. You can approve them to move to entries or delete permanently.</p>

      {posts.length === 0 ? (
        <div className="empty-state" data-testid="no-filtered-posts">
          <Filter size={48} />
          <div className="empty-text">No filtered posts yet</div>
          <div className="empty-hint">Posts rejected by AI during "Vet & Import" will appear here</div>
        </div>
      ) : (
        <div className="filtered-posts-list">
          {posts.map(post => (
            <div key={post.id} className="filtered-post-card" data-testid={`filtered-post-${post.id}`}>
              <div className="filtered-post-main">
                <div className="filtered-post-content">
                  <div className="result-title">{post.title || '(no title)'}</div>
                  <div className="result-meta">
                    <span className="subreddit-cell">r/{post.subreddit}</span>
                    <span className="result-author">u/{post.account}</span>
                    <span className="result-score"><ArrowUp size={11} /> {post.upvotes || 0}</span>
                    <span className="result-comments"><MessageSquare size={11} /> {post.comments_count || 0}</span>
                  </div>
                  {post.filter_reason && (
                    <div className="filter-reason">
                      <X size={12} color="#ef4444" /> <strong>Reason:</strong> {post.filter_reason}
                    </div>
                  )}
                  {post.relevance_score !== undefined && (
                    <div className="relevance-score">
                      Relevance: <span className={`score-badge ${post.relevance_score >= 7 ? 'high' : post.relevance_score >= 4 ? 'mid' : 'low'}`}>
                        {post.relevance_score}/10
                      </span>
                    </div>
                  )}
                </div>
                <div className="filtered-post-actions">
                  <button className="btn btn-sm btn-primary" onClick={() => handleApprove(post.id)}
                    disabled={!!actionLoading[post.id]} data-testid={`approve-${post.id}`}>
                    {actionLoading[post.id] === "approve" ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />} Approve
                  </button>
                  <button className="btn btn-sm btn-danger" onClick={() => handleDelete(post.id)}
                    disabled={!!actionLoading[post.id]} data-testid={`delete-filtered-${post.id}`}>
                    {actionLoading[post.id] === "delete" ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />} Delete
                  </button>
                  {post.url && (
                    <a href={post.url} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-secondary">
                      <ExternalLink size={12} />
                    </a>
                  )}
                  <button className="btn btn-sm btn-secondary" onClick={() => setExpandedId(expandedId === post.id ? null : post.id)}>
                    {expandedId === post.id ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                  </button>
                </div>
              </div>
              {expandedId === post.id && post.body && (
                <div className="result-body">{post.body}</div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
